/**
 * Where a passenger goes after signing in.
 *
 * Booking requires an account (DECISIONS D68), so the sign-in screen is now something people
 * arrive at mid-task rather than on purpose. The trip they picked is carried across in
 * `?next=`, and returning them to it is the difference between a login wall and a pause.
 */

/** Where sign-in leads when nothing sent the passenger there — their trips, as before. */
export const DEFAULT_NEXT = '/bookings';

/**
 * A `next` value is a destination taken from the URL bar, which means it is attacker-supplied
 * whatever put it there. Anything that is not a path ON THIS SITE is discarded rather than
 * corrected: a login form that will forward to wherever its query string says is a phishing
 * hop with our own domain and our own branding on it.
 *
 * Rejected, and why each one matters:
 *   https://evil.example  absolute, leaves the site outright
 *   //evil.example        protocol-relative; a browser reads this as a host, not a path
 *   /\evil.example        the same trick with a backslash, which some browsers normalise
 *   /auth/login           lands back on the screen the passenger just completed
 */
export function safeNext(raw: string | null | undefined, fallback = DEFAULT_NEXT): string {
  if (!raw) return fallback;

  const value = raw.trim();
  if (!value.startsWith('/')) return fallback;
  if (value.startsWith('//') || value.startsWith('/\\')) return fallback;
  // A round trip through the auth screens is not a destination, it is a loop.
  if (value === '/auth' || value.startsWith('/auth/')) return fallback;

  return value;
}

/** The current location, packed into a `next` the sign-in screen can hand back. */
export function nextParam(pathname: string, search: string): string {
  return encodeURIComponent(`${pathname}${search}`);
}
